import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  ACCOUNT_ADMIN_SYNC_KINDS,
  type AccountAdminSyncKind,
  type AccountAdminSyncMeta,
} from '@/api/accountAdminSync';
import { accountLabel, type LinkedAccountRow } from '@/api/managerAccounts';

function SyncKindIcon({ kindId }: { kindId: string }) {
  // Extend when new kinds need distinct icons; Target is the default report-sync glyph.
  void kindId;
  return <Target className="h-3.5 w-3.5 mr-1.5" aria-hidden />;
}

/** Per-account button for one registered admin sync kind. */
export function AccountAdminSyncKindButton({
  kind,
  profileId,
  meta,
  disabled,
}: {
  kind: AccountAdminSyncKind;
  profileId: string;
  meta?: AccountAdminSyncMeta;
  disabled?: boolean;
}) {
  const mutation = useMutation({
    mutationFn: async () => kind.enqueue(profileId, meta),
  });

  const errorMessage = mutation.isError ? (mutation.error as Error).message : null;

  return (
    <div className="flex flex-col items-end gap-0.5">
      {mutation.isSuccess ? (
        <span className="text-xs text-foreground-subtle max-w-[11rem] text-right truncate">
          Queued
        </span>
      ) : null}
      {errorMessage ? (
        <span className="text-xs text-warning max-w-[11rem] text-right truncate" title={errorMessage}>
          Sync failed
        </span>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={mutation.isPending || disabled}
        onClick={() => mutation.mutate()}
        className="whitespace-nowrap"
        title={kind.title}
      >
        {mutation.isPending ? (
          <>
            <Spinner className="h-3.5 w-3.5 mr-1.5" />
            Syncing…
          </>
        ) : (
          <>
            <SyncKindIcon kindId={kind.id} />
            {kind.label}
          </>
        )}
      </Button>
    </div>
  );
}

/** All registered per-row admin sync buttons for one account. */
export function AccountAdminSyncButtons({
  profileId,
  meta,
  disabled,
}: {
  profileId: string;
  meta?: AccountAdminSyncMeta;
  disabled?: boolean;
}) {
  if (ACCOUNT_ADMIN_SYNC_KINDS.length === 0) return null;
  return (
    <>
      {ACCOUNT_ADMIN_SYNC_KINDS.map((kind) => (
        <AccountAdminSyncKindButton
          key={kind.id}
          kind={kind}
          profileId={profileId}
          meta={meta}
          disabled={disabled}
        />
      ))}
    </>
  );
}

type BulkProgress = { kindId: string; done: number; total: number; failed: number };

/** Master panel: one “sync all” control per registered kind. */
export function AccountAdminSyncMasterPanel({
  rows,
  onPendingChange,
}: {
  rows: Array<LinkedAccountRow & { profileId: string }>;
  onPendingChange?: (pending: boolean) => void;
}) {
  const [progress, setProgress] = useState<BulkProgress | null>(null);
  const [activeKindId, setActiveKindId] = useState<string | null>(null);

  const bulkMutation = useMutation({
    mutationFn: async (kind: AccountAdminSyncKind) => {
      setActiveKindId(kind.id);
      const total = rows.length;
      let done = 0;
      let failed = 0;
      setProgress({ kindId: kind.id, done: 0, total, failed: 0 });
      for (const row of rows) {
        try {
          await kind.enqueue(row.profileId, {
            accountName: accountLabel(row),
            managerAccountName: row.managerAccountName ?? undefined,
          });
        } catch {
          failed += 1;
        }
        done += 1;
        setProgress({ kindId: kind.id, done, total, failed });
      }
      return { done, total, failed };
    },
    onSettled: () => {
      setActiveKindId(null);
    },
  });

  useEffect(() => {
    onPendingChange?.(bulkMutation.isPending);
  }, [bulkMutation.isPending, onPendingChange]);

  if (ACCOUNT_ADMIN_SYNC_KINDS.length === 0 || rows.length === 0) return null;

  const anyRunning = bulkMutation.isPending;

  return (
    <div className="mb-6 space-y-3">
      {ACCOUNT_ADMIN_SYNC_KINDS.map((kind) => {
        const kindProgress = progress?.kindId === kind.id ? progress : null;
        const isThisRunning = anyRunning && activeKindId === kind.id;
        return (
          <div
            key={kind.id}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{kind.panelTitle}</p>
              <p className="text-xs text-foreground-subtle mt-0.5">{kind.description}</p>
              {kindProgress ? (
                <p className="text-xs text-foreground-muted mt-1">
                  Progress: {kindProgress.done}/{kindProgress.total}
                  {kindProgress.failed > 0 ? ` (${kindProgress.failed} failed)` : ''}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={anyRunning}
              onClick={() => bulkMutation.mutate(kind)}
              title={kind.title}
            >
              {isThisRunning ? (
                <>
                  <Spinner className="h-3.5 w-3.5 mr-1.5" />
                  Syncing all…
                </>
              ) : (
                <>
                  <SyncKindIcon kindId={kind.id} />
                  {kind.masterLabel}
                </>
              )}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
